/** @jsx jsx */
import { css, jsx } from '@emotion/react';
import React, { useContext, useRef, useMemo, useEffect } from 'react';
import { WIDGETS } from '/widgets';
import { NonIdealState } from '@blueprintjs/core';
import LoadingIcon from 'components/loadingIcon';
import { centerContent } from 'styles/styledComponentUtils';
import CardContext from '../explorer/cardContext';
import { ColorScalesContext } from 'views/common/colorScalesContext';
import { WorkspaceActionCreator } from 'hooks/useWorkspaceState';
import { WorkspaceContext } from 'views/workspace/workspaceContext';
import { NO_DATA_MESSAGE } from 'widgets/chartUtils';

/**
 * WidgetContainer is responsible for instantiating the correct WidgetWrapper object
 * and wrapping it in the appropriate loading state and error boundaries.  The WidgetWrapper
 * will convert the endpoint-specific data structure into something specific to the Widget.
 *
 * @param loading
 * @param hasError
 * @param data
 * @param widgetConfiguration
 * @param dimensions
 * @param type
 * @returns {*}
 * @constructor
 */
export function WidgetContainer({
	loading,
	data,
	noDataMessage = NO_DATA_MESSAGE,
	selectedDefinitionName,
	widgetConfiguration,
	endpointQuery,
	dimensions,
	type,
	cardIndex,
	actions,
}) {
	const WidgetWrapper = WIDGETS[type].wrapper;
	const workspaceContext = useContext(WorkspaceContext);
	const cardContext = useContext(CardContext); //may or may not be available depending on parents.
	//Go grab the app specific color scale method. Initialize it with serberus' default colors. If the app has its own colors they will be used instead
	const colorChain = useContext(ColorScalesContext);
	const getColor = useMemo(() => {
		if(!widgetConfiguration) //can be null first render, it comes around
			return colorChain.execute(type /*Will pick first available scale for the provided type/widget*/);

		//If widgetConfig has filtered the available scales do that here.
		colorChain.filter(type, widgetConfiguration.availableColorScales);
		return colorChain.execute(type, widgetConfiguration.colorScale);
	},[/*colorChain, type,*/ widgetConfiguration]);

	useEffect(() => {
		//If widgetConfiguration doesn't exist just by pass. It won't exist on first render at start up but subsequent render it will
		if(workspaceContext && widgetConfiguration && getColor.scaleId !== widgetConfiguration.colorScale && cardIndex !== undefined) {
			const { dispatch } = workspaceContext;
			//can happen if colorScale specified a scale that isn't available
			const configuration = JSON.parse(JSON.stringify(widgetConfiguration));
			configuration.colorScale = getColor.scaleId;
			dispatch(WorkspaceActionCreator.configuration(cardIndex, configuration));
		}
	}, [widgetConfiguration, workspaceContext, cardIndex/*, getColor.scaleId*/]);


	//Currently only ExplorerCard passes in CardContext, Screen and Dashboard Cards do not, so have a fall back
	const { isSettingsDialogOpen: pauseUpdates } = cardContext || { isSettingsDialogOpen: false };

	const wrapperRef = useRef();
	const isNonIdealState = !loading && !data;
	if (!pauseUpdates && !isNonIdealState) {
		wrapperRef.current = (
			//data will look different for each widget
			<WidgetWrapper
				data={data}
				widgetConfiguration={widgetConfiguration}
				endpointQuery={endpointQuery}
				actions={actions}
				dimensions={dimensions}
				loading={loading}
				cardIndex={cardIndex}
				selectedDefinitionName={selectedDefinitionName}
				getColorScale={getColor}
			/>
		);
	}

	return (
		<React.Fragment>
			{data && wrapperRef.current}
			{/* Loading states are managed here. A large spinner is rendered when the widget output isn't shown.
        A small spinner is rendered when the widget is waiting for newer data.*/}
			{isNonIdealState && (
				// Handle the "No Data" state
				<NonIdealState
					title={'No data found'}
					icon={'clean'}
					description={noDataMessage}
				/>
			)}
			{loading &&
				(data ? (
					<div cy-test="loading-container" css={loadingContainer}>
						<LoadingIcon size={15} />
					</div>
				) : (
					<div css={spreadContainer}>
						<LoadingIcon />
					</div>
				))}
		</React.Fragment>
	);
}

const loadingContainer = css`
	position: absolute;
	top: 0;
	left: 0;
`;

const spreadContainer = css`
	position: absolute;
	top: 0;
	left: 0;
	bottom: 0;
	right: 0;
	${centerContent};
`;
